import classNames from 'classnames/bind';
import React, { useMemo, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import styles from './common.module.scss';

import actions from '@/actions';
import FieldRow from '@/components/ui/FieldRow';
import { FieldNumber } from '@/components/ui/kit/Field';
import { SURVEY_ENTRIES, SURVEY_GAP_MIN, SURVEY_GAP_MAX, SURVEY_TURNAROUND_MIN, SURVEY_TURNAROUND_MAX } from '@/config';
import { NotifierService as notifier } from '@/libs/Notifier';
import { getPolygonArea } from '@/utils/MapUtils';
import { deepCopy } from '@/utils/ObjectUtils';
import { getPositions } from '@/utils/SurveyCalculator';

const cx = classNames.bind(styles);

const Command = ({ index, data: command }) => {
  const dispatch = useDispatch();
  const domRefs = useRef({
    relAltitude: useRef(),
    gap: useRef(),
    turnaround: {
      before: useRef(),
      after: useRef(),
    },
    rotate: useRef(),
  }).current;

  // 면적
  const area = useMemo(() => {
    return getPolygonArea(command.data.boundary);
  }, [command.data.positions]);

  useEffect(() => {
    setFieldsToOrigin();
  }, [command.data]);

  const setFieldsToOrigin = () => {
    domRefs.relAltitude.current.value = command.data.altitude.toFixed(1);
    domRefs.gap.current.value = command.data.gap.toFixed(1);
    domRefs.turnaround.before.current.value = command.data.turnaround.before.toFixed(1);
    domRefs.turnaround.after.current.value = command.data.turnaround.after.toFixed(1);
    domRefs.rotate.current.value = command.data.rotate;
  };

  const rotateEntryPoint = () => {
    const entryIndex = (command.data.entryIndex + 1) % SURVEY_ENTRIES.length;
    const positions = getPositionsByChangedOption({ entryIndex });

    editSurvey({ entryIndex, positions });
  };

  const changeRelAltitude = (e) => {
    editSurvey({ altitude: Number(e.target.value) });
  };

  const changeGap = (e) => {
    let gap = Number(e.target.value);
    gap = Math.max(gap, SURVEY_GAP_MIN);
    gap = Math.min(gap, SURVEY_GAP_MAX);

    const positions = getPositionsByChangedOption({ gap });
    editSurvey({ gap, positions });
  };

  const changeTurnaround = (e) => {
    const name = e.target.name.split('.')[1];

    let value = Number(e.target.value);
    value = Math.max(value, SURVEY_TURNAROUND_MIN);
    value = Math.min(value, SURVEY_TURNAROUND_MAX);

    const turnaround = deepCopy(command.data.turnaround);
    turnaround[name] = value;
    const positions = getPositionsByChangedOption({ turnaround });
    editSurvey({ turnaround, positions });
  };

  const changeRotate = (e) => {
    let rotate = Math.trunc(e.target.value) % 360;
    if (rotate < 0) {
      rotate = 360 - Math.abs(rotate);
    }
    rotate %= 180;

    const positions = getPositionsByChangedOption({ rotate });
    editSurvey({ rotate, positions });
  };

  const editSurvey = (values) => {
    if (values.positions?.length === 0) {
      notifier.error('The area is too small.');
      setFieldsToOrigin();
      return;
    }

    dispatch(actions.mission.editSurvey(index, values));
  };

  const getPositionsByChangedOption = (values) => {
    const { boundary, entryIndex, gap, turnaround, rotate } = command.data;

    return getPositions(boundary, {
      entryIndex,
      gap,
      turnaround,
      rotate,
      ...values,
    });
  };

  return (
    <div className={cx('container')}>
      <button type="button" className={cx('button')} onClick={rotateEntryPoint}>
        Rotate Entry Point
      </button>
      <FieldRow label="Area" unit="㎢">
        <FieldNumber value={area.toFixed(1)} readOnly />
      </FieldRow>
      <FieldRow label="Relative Altitude" unit="m">
        <FieldNumber ref={domRefs.relAltitude} name="relAltitude" onBlur={changeRelAltitude} />
      </FieldRow>
      <FieldRow label="Gap" unit="m">
        <FieldNumber ref={domRefs.gap} name="gap" onBlur={changeGap} />
      </FieldRow>
      <FieldRow label="Before turnaround" unit="m">
        <FieldNumber ref={domRefs.turnaround.before} name="turnaround.before" onBlur={changeTurnaround} />
      </FieldRow>
      <FieldRow label="After turnaround" unit="m">
        <FieldNumber ref={domRefs.turnaround.after} name="turnaround.after" onBlur={changeTurnaround} />
      </FieldRow>
      <FieldRow label="Rotate" unit="°">
        <FieldNumber ref={domRefs.rotate} name="rotate" onBlur={changeRotate} />
      </FieldRow>
    </div>
  );
};

export default Command;
